/**
 * Barre d'action des favoris, au-dessus de la liste quand le filtre est actif.
 *
 * « Tout ajouter au panier » n'ajoute QUE les articles vendus à la pièce, et
 * NOMME ceux qui se vendent au poids au lieu de deviner une quantité.
 *
 * Pourquoi : 255 des 1 471 articles actifs se vendent au poids — et ce sont
 * les fruits et légumes, donc précisément ce qu'une cliente régulière met dans
 * ses favoris. Leur inventer un kilo par défaut remplirait le panier de
 * quantités que personne n'a choisies, sur les articles les plus chers au
 * kilo. Les nommer coûte un geste ; les deviner coûterait une réclamation.
 */

import type { Product } from '../../../types/ecommerce.types';

interface BarreFavorisProps {
  produits: Product[];
  onToutAjouter: (aLaPiece: Product[]) => void;
  onChoisirPoids: (produit: Product) => void;
  /** Sans compte, la liste ne vit que dans ce navigateur : le dire. */
  connecte?: boolean;
}

const estAuPoids = (p: Product) =>
  // OU, jamais `??` : `is_weighted` vaut false par défaut et court-circuiterait
  // le test sur l'unité (même piège que la caisse, cf. CLAUDE.md).
  p.is_weighted === true || p.unit === 'kg';

export default function BarreFavoris({ produits, onToutAjouter, onChoisirPoids, connecte = true }: BarreFavorisProps) {
  const noteInvite = !connecte && (
    <p className="text-sm text-[#6b6550] mt-2">
      Vos favoris sont gardés sur cet appareil. Connectez-vous pour les retrouver sur tous vos appareils.
    </p>
  );

  const auPoids = produits.filter(estAuPoids);
  const aLaPiece = produits.filter((p) => !estAuPoids(p));

  if (produits.length === 0) {
    return (
      <div className="bg-[#f6e6da] border border-[#e0c3ab] rounded-xl px-4 py-4 mb-4">
        <p className="text-[#8f4a22] font-semibold">Votre liste de favoris est vide.</p>
        <p className="text-sm text-[#6b6550] mt-1">
          Touchez le cœur sur un article pour l'y ajouter — vous le retrouverez ici à chaque visite.
        </p>
        {noteInvite}
      </div>
    );
  }

  return (
    <div className="bg-[#f6e6da] border border-[#e0c3ab] rounded-xl px-4 py-3 mb-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-semibold text-[#8f4a22]">
          {produits.length} favori{produits.length > 1 ? 's' : ''} disponible
          {produits.length > 1 ? 's' : ''}
        </span>
        {aLaPiece.length > 0 && (
          <button
            type="button"
            onClick={() => onToutAjouter(aLaPiece)}
            className="ml-auto px-4 py-2 rounded-lg bg-[#b45f2e] text-white font-bold active:bg-[#9a5027] min-h-[44px]"
          >
            Ajouter les {aLaPiece.length} article{aLaPiece.length > 1 ? 's' : ''} à la pièce
          </button>
        )}
      </div>

      {auPoids.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#e0c3ab]">
          <p className="text-sm text-[#6b6550] mb-2">
            {auPoids.length} article{auPoids.length > 1 ? 's' : ''} se vend
            {auPoids.length > 1 ? 'ent' : ''} au poids — choisissez la quantité :
          </p>
          <div className="flex flex-wrap gap-2">
            {auPoids.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onChoisirPoids(p)}
                className="px-3 py-2 rounded-lg bg-white border border-[#e0c3ab] text-[#8f4a22] text-sm font-semibold active:bg-[#f0e9d8] min-h-[40px]"
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {noteInvite}
    </div>
  );
}
